'use client'
import MainMenu from '@/components/MainMenu';
import Footer from '@/components/Footer';
import usePhoneNumber from '@/hooks/usePhoneNumber';
import { useCallback, useEffect, useState } from 'react';
import { sendRequestNoToken } from '@/utils/apiService';
import Select from 'react-select';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCross, faX } from '@fortawesome/free-solid-svg-icons';
import Link from 'next/link';
import { prependCoreUrl } from '@/utils/globals';

interface School {
    id: number;
    name: string;
    country_tag: string;
    one_credit: number;
    all_credit: number;
    image: string;
    text: string;
    courses: string[];
    short_description: string;
}

interface Course {
    id: number;
    name: string;
    primary_subject_choices: string[];
    primary_subject_point: number;
    secondary_subject_choices: string[];
    secondary_subject_point: number;
    study_year: number;
}

const MySchoolPage = () => {
    const { phoneNumber, isCookieLoading } = usePhoneNumber();
    const [isLoading, setIsLoading] = useState(true);
    const [schools, setSchools] = useState<School[]>([]);
    const [courses, setCourses] = useState<{ value: string; label: string }[]>([]);
    const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);
    const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<{ value: string; label: string } | null>(null);
    const [filteredSchools, setFilteredSchools] = useState<School[]>([]);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchData = useCallback(() => {
        setIsLoading(true);
        sendRequestNoToken("GET", `/api/schools/`, null)
            .then((data) => {
                setSchools(data.schools);
                setFilteredSchools(data.schools);

                const formattedCourses = data.courses.map((course: string) => ({
                    value: course.toLowerCase(),
                    label: course,
                }));
                setCourses(formattedCourses);

                const formattedCountries = data.countries.map((country: string) => ({
                    value: country.toLowerCase(),
                    label: country,
                }));
                setCountries(formattedCountries);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!schools || !Array.isArray(schools)) return;

        const lowerCaseSearchTerm = searchTerm.toLowerCase(); // Ensure searchTerm is lowercase

        setFilteredSchools(
            schools.filter((school) => {
                const schoolNameMatch = school?.name?.toLowerCase().includes(lowerCaseSearchTerm);

                const courseMatch = selectedOption
                    ? school?.courses?.some((course) =>
                        course.toLowerCase().includes(selectedOption.value)
                    )
                    : true;

                const countryMatch = selectedCountry
                    ? school.country_tag.toLowerCase() === selectedCountry.value
                    : true;

                return schoolNameMatch && courseMatch && countryMatch;
            })
        );
    }, [searchTerm, schools, selectedOption, selectedCountry]);

    const resetFilters = () => {
        setSearchTerm("");
        setSelectedOption(null);
        setSelectedCountry(null);
        setFilteredSchools(schools);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-dark-800">
                <div className="text-center">
                    <div className="spinner-border animate-spin inline-block w-16 h-16 border-8 border-solid border-purple-500 border-t-transparent rounded-full"></div>
                    <p className="mt-4 text-xl dark:text-dark-100 text-gray-700">Уншиж байна...</p>
                </div>
            </div>
        );
    }


    return (
        <main className="flex min-h-screen flex-col items-center justify-between overflow-hidden">
            <MainMenu isLoading={isCookieLoading} phoneNumber={phoneNumber} scrollIndicatorEnabled={true} />
            <section className="text-start w-screen justify-start bg-gradient-start min-h-screen dark:bg-dark-800">
                <div className='container mt-28 mb-10 text-center'>
                    <div>
                        <div className='text-4xl md:text-6xl font-semibold'>
                            my<span className='text-purple-500'>Education</span>
                        </div>
                        <div className='text-lg mt-10 max-w-[600px] mx-auto'>
                            Монгол болон дэлхийн их сургууль, коллежуудын талаарх мэдээлэл
                        </div>
                    </div>
                </div>
                <div className="mb-28">
                    <div className="space-y-6">
                        <div className='container flex flex-col md:flex-row md:space-x-4 space-y-2 md:space-y-0 text-xs'>
                            <input
                                type="text"
                                placeholder="Сургууль хайх"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="p-3 border rounded-lg border-gray-600 outline-purple-500 w-full md:w-1/2 placeholder:text-dark-700 dark:text-dark-700"
                            />
                            <Select
                                value={selectedOption}
                                onChange={setSelectedOption}
                                options={courses}
                                placeholder="Хөтөлбөр сонгох"
                                className="w-full md:w-1/3 rounded"
                                styles={{
                                    control: (provided, state) => ({
                                        ...provided,
                                        borderRadius: '0.5rem',
                                        padding: '4px',
                                        borderColor: state.isFocused ? '#8646f4' : '#afafaf',
                                        boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
                                        '&:hover': {
                                            borderColor: '#8646f4',
                                        },
                                    }),
                                    input: (provided) => ({
                                        ...provided,
                                        padding: '4px',
                                    }),
                                    placeholder: (provided) => ({
                                        ...provided,
                                        color: '#718096',
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        color: '#111928',
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        backgroundColor: 'white',
                                        color: '#111',
                                    }),
                                    menuList: (provided) => ({
                                        ...provided,
                                        padding: '0',
                                    }),
                                }}
                            />
                            <Select
                                value={selectedCountry}
                                onChange={setSelectedCountry}
                                options={countries}
                                placeholder="Улс сонгох"
                                className="w-full md:w-1/3 rounded"
                                styles={{
                                    control: (provided, state) => ({
                                        ...provided,
                                        borderRadius: '0.5rem',
                                        padding: '4px',
                                        borderColor: state.isFocused ? '#8646f4' : '#afafaf',
                                        boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
                                        '&:hover': {
                                            borderColor: '#8646f4',
                                        },
                                    }),
                                    input: (provided) => ({
                                        ...provided,
                                        padding: '4px',
                                    }),
                                    placeholder: (provided) => ({
                                        ...provided,
                                        color: '#718096',
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        color: '#111928',
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        backgroundColor: 'white',
                                        color: '#111',
                                    }),
                                    menuList: (provided) => ({
                                        ...provided,
                                        padding: '0',
                                    }),
                                }}
                            />
                            <button
                                onClick={resetFilters}
                                className="bg-career-dark-200 text-white px-5 rounded-lg text-xs hidden md:flex justify-center items-center"
                            >
                                <FontAwesomeIcon icon={faX} />
                            </button>
                            <button
                                onClick={resetFilters}
                                className="bg-career-dark-200 text-white py-3 rounded-lg text-xs w-full md:hidden flex justify-center items-center"
                            >
                                <FontAwesomeIcon icon={faX} className="mr-2" />
                                <span>Хайлт цэвэрлэх</span>
                            </button>
                        </div>


                        <div className='container' style={{ scrollMarginTop: '6rem' }} id='career'>
                            <div className='grid grid-cols-1 gap-y-6 gap-x-4'>
                                {filteredSchools.map((item, index) => (
                                    <div key={index} className='bg-white dark:bg-dark-600 rounded-md shadow flex flex-col justify-between md:bg-white md:rounded-md overflow-hidden p-4'>
                                        <div className='flex flex-row space-x-2 items-center'>
                                            <div className='flex-shrink-0 bg-gray-400 rounded overflow-hidden'>
                                                <Image src={item.image} width={80} height={80} alt={item.name} />
                                            </div>
                                            <div>
                                                {item.country_tag.length > 0 && (
                                                    <div className='text-sm text-gray-700'>{item.country_tag} улс</div>
                                                )}
                                                <div className='font-semibold text-md'>{item.name}</div>

                                            </div>
                                        </div>
                                        <div className='mt-4 text-sm overflow-hidden' dangerouslySetInnerHTML={{ __html: prependCoreUrl(item.short_description) || "" }}></div>
                                        {/* <div className='text-sm mt-2'>Сургалтын төлбөр: <span className='font-semibold'>{item.all_credit}</span></div> */}
                                        {/* <div className='mt-2 text-sm overflow-hidden'><span className='font-semibold'>Хөтөлбөрүүд: </span>{item.courses.join(", ")}</div> */}
                                        <div className='flex justify-end mt-3'>
                                            <Link href={`/myeducation/${item.id}`}><button className='py-2 px-5 text-sm bg-purple-500 text-white rounded'>Дэлгэрэнгүй</button></Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer showNavigation={false} />
        </main>
    );
}

export default MySchoolPage;
